import * as THREE from 'three';

/**
 * ExplosionPatterns类 - 不同烟花爆炸形状的算法实现
 * 
 * 职责:
 * - 实现球形爆炸算法（均匀球面分布）
 * - 实现环形爆炸算法（水平圆环分布）
 * - 实现柳树形爆炸算法（向上后下垂）
 * - 实现棕榈形爆炸算法（多个向上粒子束）
 * 
 * 验证需求: Requirements 6.1, 6.2, 6.3, 6.4
 */
class ExplosionPatterns {
  /**
   * 球形爆炸 - 粒子均匀分布在球面上
   * @param {THREE.Vector3} center - 爆炸中心位置
   * @param {number} count - 粒子数量
   * @param {number} speed - 粒子速度（单位/秒）
   * @returns {Array<{position: THREE.Vector3, velocity: THREE.Vector3}>}
   */
  static sphere(center, count, speed = 8) {
    const particles = [];
    
    // 使用黄金螺旋算法实现均匀球面分布
    const goldenRatio = (1 + Math.sqrt(5)) / 2;
    const angleIncrement = Math.PI * 2 * goldenRatio;
    
    for (let i = 0; i < count; i++) {
      const t = i / count;
      const inclination = Math.acos(1 - 2 * t);
      const azimuth = angleIncrement * i;
      
      // 球面坐标转笛卡尔坐标
      const x = Math.sin(inclination) * Math.cos(azimuth);
      const y = Math.sin(inclination) * Math.sin(azimuth);
      const z = Math.cos(inclination);
      
      // 添加随机变化（5-10单位/秒）
      const randomSpeed = speed + (Math.random() - 0.5) * 4;
      
      particles.push({
        position: center.clone(),
        velocity: new THREE.Vector3(x, y, z).multiplyScalar(randomSpeed)
      });
    }
    
    return particles;
  }

  /**
   * 环形爆炸 - 粒子主要分布在水平圆环上
   * @param {THREE.Vector3} center - 爆炸中心位置
   * @param {number} count - 粒子数量
   * @param {number} speed - 粒子速度（单位/秒）
   * @param {number} radius - 环半径
   * @returns {Array<{position: THREE.Vector3, velocity: THREE.Vector3}>}
   */
  static ring(center, count, speed = 10, radius = 1) {
    const particles = [];
    
    // 80%的粒子在水平环上
    const ringCount = Math.floor(count * 0.8);
    for (let i = 0; i < ringCount; i++) {
      const angle = (i / ringCount) * Math.PI * 2;
      
      // 水平方向
      const x = Math.cos(angle);
      const z = Math.sin(angle);
      const y = (Math.random() - 0.5) * 0.3; // 轻微的垂直变化
      
      const randomSpeed = speed + (Math.random() - 0.5) * 4;
      
      particles.push({
        position: center.clone(),
        velocity: new THREE.Vector3(x, y, z).normalize().multiplyScalar(randomSpeed)
      });
    }
    
    // 20%的粒子向上和向下
    const verticalCount = count - ringCount;
    for (let i = 0; i < verticalCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const x = Math.cos(angle) * 0.3;
      const z = Math.sin(angle) * 0.3;
      const y = Math.random() > 0.5 ? 1 : -1;
      
      const randomSpeed = speed * 0.6;
      
      particles.push({
        position: center.clone(),
        velocity: new THREE.Vector3(x, y, z).normalize().multiplyScalar(randomSpeed)
      });
    }
    
    return particles;
  }

  /**
   * 柳树形爆炸 - 粒子初始向上，然后下垂
   * @param {THREE.Vector3} center - 爆炸中心位置
   * @param {number} count - 粒子数量
   * @param {number} speed - 粒子速度（单位/秒）
   * @returns {Array<{position: THREE.Vector3, velocity: THREE.Vector3}>}
   */
  static willow(center, count, speed = 7) {
    const particles = [];
    
    for (let i = 0; i < count; i++) {
      // 主要向上，带有轻微的横向扩散
      const angle = Math.random() * Math.PI * 2;
      const horizontalSpread = Math.random() * 0.4;
      
      const x = Math.cos(angle) * horizontalSpread;
      const z = Math.sin(angle) * horizontalSpread;
      const y = 0.8 + Math.random() * 0.4; // 主要向上
      
      const randomSpeed = speed + (Math.random() - 0.5) * 3;
      
      particles.push({
        position: center.clone(),
        velocity: new THREE.Vector3(x, y, z).normalize().multiplyScalar(randomSpeed)
      });
    }
    
    return particles;
  }

  /**
   * 棕榈形爆炸 - 多个向上的粒子束
   * @param {THREE.Vector3} center - 爆炸中心位置
   * @param {number} count - 粒子数量
   * @param {number} speed - 粒子速度（单位/秒）
   * @returns {Array<{position: THREE.Vector3, velocity: THREE.Vector3}>}
   */
  static palm(center, count, speed = 9) {
    const particles = [];
    
    // 创建5-8个粒子束
    const beamCount = 5 + Math.floor(Math.random() * 4);
    const particlesPerBeam = Math.floor(count / beamCount);
    
    for (let beam = 0; beam < beamCount; beam++) {
      // 每个束的基础方向
      const beamAngle = (beam / beamCount) * Math.PI * 2;
      const beamTilt = 0.3 + Math.random() * 0.3; // 束的倾斜度
      
      const beamDirX = Math.cos(beamAngle) * beamTilt;
      const beamDirZ = Math.sin(beamAngle) * beamTilt;
      const beamDirY = 0.9; // 主要向上
      
      const beamDirection = new THREE.Vector3(beamDirX, beamDirY, beamDirZ).normalize();
      
      // 在每个束中创建粒子
      for (let i = 0; i < particlesPerBeam; i++) {
        // 在束方向周围添加轻微随机偏移
        const offset = new THREE.Vector3(
          (Math.random() - 0.5) * 0.2,
          (Math.random() - 0.5) * 0.2,
          (Math.random() - 0.5) * 0.2
        );
        
        const velocity = beamDirection.clone().add(offset).normalize();
        const randomSpeed = speed + (Math.random() - 0.5) * 3;
        
        particles.push({
          position: center.clone(),
          velocity: velocity.multiplyScalar(randomSpeed)
        });
      }
    }
    
    // 填充剩余的粒子
    const remaining = count - particles.length;
    for (let i = 0; i < remaining; i++) {
      const angle = Math.random() * Math.PI * 2;
      const tilt = Math.random() * 0.4;
      
      const x = Math.cos(angle) * tilt;
      const z = Math.sin(angle) * tilt;
      const y = 0.9;
      
      const randomSpeed = speed + (Math.random() - 0.5) * 3;
      
      particles.push({
        position: center.clone(),
        velocity: new THREE.Vector3(x, y, z).normalize().multiplyScalar(randomSpeed)
      });
    }
    
    return particles;
  }

  /**
   * 随机选择一个爆炸形状
   * @returns {string} 形状名称
   */
  static getRandomPattern() {
    const patterns = ['sphere', 'ring', 'willow', 'palm'];
    return patterns[Math.floor(Math.random() * patterns.length)];
  }

  /**
   * 根据形状名称创建爆炸
   * @param {string} patternName - 形状名称
   * @param {THREE.Vector3} center - 爆炸中心位置
   * @param {number} count - 粒子数量
   * @param {number} speed - 粒子速度
   * @returns {Array<{position: THREE.Vector3, velocity: THREE.Vector3}>}
   */
  static createPattern(patternName, center, count, speed) {
    switch (patternName) {
      case 'sphere':
        return ExplosionPatterns.sphere(center, count, speed);
      case 'ring':
        return ExplosionPatterns.ring(center, count, speed);
      case 'willow':
        return ExplosionPatterns.willow(center, count, speed);
      case 'palm':
        return ExplosionPatterns.palm(center, count, speed);
      default:
        console.warn(`Unknown pattern: ${patternName}, using sphere`);
        return ExplosionPatterns.sphere(center, count, speed);
    }
  }
}

export default ExplosionPatterns;
